import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Smartphone, CreditCard, RefreshCw, XCircle, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Cobro con terminal física Mercado Pago Point para ventas directas (Retail / Servicios).
 * Reutiliza los MISMOS endpoints del flujo Point existente (devices, cobrar, estado, cancelar).
 * NO registra la venta hasta que Mercado Pago aprueba el pago (el backend finaliza en /point/estado).
 */
export default function PointCobroModal({
  open, onClose, idSucursal, idCaja, monto,
  productosVendidos = [], serviciosVendidos = [],
  referencia = "VENTA DIRECTA", titulo = "Cobro con tarjeta",
  onApproved,
}) {
  const [devices, setDevices] = useState([]);
  const [device, setDevice] = useState("");
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [status, setStatus] = useState("idle"); // idle|sending|waiting|timeout|error
  const [txId, setTxId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [msg, setMsg] = useState("");
  const pollRef = useRef(null);
  const deviceKey = `pap_point_device_${idSucursal}`;

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const resetState = useCallback(() => {
    stopPoll();
    setStatus("idle"); setTxId(null); setOrderId(null); setOrderStatus(null); setMsg("");
  }, []);

  const cargarDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const { data } = await api.get(`/mercadopago/point/devices?id_sucursal=${idSucursal}`);
      const list = data.devices || [];
      setDevices(list);
      const saved = sessionStorage.getItem(deviceKey) || "";
      if (saved && list.some((d) => d.id === saved)) setDevice(saved);
      else if (list.length === 1) setDevice(list[0].id);
    } catch (err) {
      setDevices([]);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setDevicesLoading(false); }
  }, [idSucursal, deviceKey]);

  useEffect(() => {
    if (open) { resetState(); cargarDevices(); }
    return () => stopPoll();
  }, [open, resetState, cargarDevices]);

  const aplicarEstado = useCallback((data) => {
    setOrderStatus(data.order_status || null);
    setMsg(data.mensaje || "");
    if (data.estado === "approved") {
      stopPoll();
      onApproved?.(data);
    } else if (data.estado === "expired") {
      stopPoll(); setStatus("error"); setMsg(data.mensaje || "La orden expiró. Genera un nuevo cobro.");
    } else if (["rejected", "canceled"].includes(data.estado)) {
      stopPoll(); setStatus("error");
    }
  }, [onApproved]);

  const consultarEstado = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/mercadopago/point/estado?id_transaccion=${id}`);
      aplicarEstado(data);
      return data;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return null; }
  }, [aplicarEstado]);

  const iniciarPolling = useCallback((id) => {
    stopPoll();
    const inicio = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - inicio > 300000) {
        stopPoll();
        const d = await consultarEstado(id);
        const est = d?.estado;
        if (["approved", "rejected", "canceled", "expired"].includes(est)) return;
        setStatus("timeout");
        return;
      }
      await consultarEstado(id);
    }, 2000);
  }, [consultarEstado]);

  const enviarCobro = async () => {
    const dev = device.trim();
    if (!dev) return toast.error("Selecciona la terminal");
    if (!idCaja) return toast.error("No hay una caja/turno activo");
    sessionStorage.setItem(deviceKey, dev);
    setStatus("sending"); setMsg(""); setOrderStatus(null);
    try {
      const { data } = await api.post("/mercadopago/point/cobrar", {
        id_sucursal: idSucursal, id_caja: idCaja, monto: Number(monto), id_device: dev,
        referencia, productos_vendidos: productosVendidos, servicios_adicionales_vendidos: serviciosVendidos,
      });
      setTxId(data.id_transaccion);
      setOrderId(data.order_id || null);
      setOrderStatus(data.order_status || "created");
      setStatus("waiting");
      iniciarPolling(data.id_transaccion);
    } catch (err) {
      setStatus("error");
      setMsg(formatApiError(err.response?.data?.detail));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const consultarManual = async () => {
    if (!txId) return;
    const data = await consultarEstado(txId);
    if (data && ["created", "at_terminal", "action_required"].includes(data.estado)) {
      setStatus("waiting"); iniciarPolling(txId);
    }
  };

  const cancelarPendiente = async () => {
    if (!txId) return;
    try {
      const { data } = await api.post(`/mercadopago/point/cancelar?id_transaccion=${txId}`);
      toast[data.ok ? "success" : "warning"](data.mensaje || "");
      stopPoll();
      if (data.ok && data.estado === "canceled") { setStatus("error"); setMsg(data.mensaje || "Cobro cancelado y confirmado por Mercado Pago."); }
      else if (data.estado === "expired") { setStatus("error"); setMsg(data.mensaje || "La orden expiró; se registró como Expirada."); }
      else if (data.estado === "processed") { consultarEstado(txId); }
      else { setStatus("waiting"); setMsg(data.mensaje || ""); iniciarPolling(txId); }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cerrar = () => { stopPoll(); onClose?.(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cerrar(); }}>
      <DialogContent className="rounded-none border-[#009EE3]/40 bg-[#0A0A0A] text-white sm:max-w-md max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="point-directo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-white flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-[#38BDF8]" /> {titulo}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            El cobro se envía a la terminal Mercado Pago. La venta se registra sólo cuando el pago es aprobado.
          </DialogDescription>
        </DialogHeader>

        {status === "idle" && (
          <div className="space-y-4 pt-1" data-testid="point-directo-idle">
            <div className="flex items-center justify-between border-2 border-[#FFCC00]/60 bg-[#FFCC00]/5 p-3">
              <span className="text-sm font-bold uppercase tracking-wider text-[#FFCC00]">Total a cobrar</span>
              <span className="font-display font-black text-2xl text-[#FFCC00]" data-testid="point-directo-total">${Number(monto || 0).toFixed(2)}</span>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-white/60">Terminal Point</label>
              {devicesLoading ? (
                <div className="mt-2 flex items-center gap-2 text-white/60 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Buscando terminales…</div>
              ) : devices.length === 0 ? (
                <div className="mt-1 space-y-2" data-testid="point-directo-no-devices">
                  <p className="text-sm text-[#F87171]">No se encontraron terminales Point en esta cuenta.</p>
                </div>
              ) : (
                <Select value={device} onValueChange={(v) => { setDevice(v); sessionStorage.setItem(deviceKey, v); }}>
                  <SelectTrigger data-testid="point-directo-device-select" className="mt-1 h-12 rounded-none bg-white/5 border-white/20 text-white font-mono">
                    <SelectValue placeholder="Selecciona una terminal" />
                  </SelectTrigger>
                  <SelectContent>
                    {devices.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`point-directo-device-${d.id}`}>{d.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <button onClick={enviarCobro} disabled={!device} data-testid="point-directo-enviar-btn"
              className="btn-hard w-full h-14 bg-[#009EE3] text-white border border-[#009EE3] font-black uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50">
              <Smartphone className="h-5 w-5" /> Enviar a terminal
            </button>
          </div>
        )}

        {(status === "sending" || status === "waiting") && (
          <div className="py-6 flex flex-col items-center text-center gap-5" data-testid="point-directo-waiting">
            <div className="relative">
              <Smartphone className="h-16 w-16 text-[#38BDF8]" strokeWidth={1.5} />
              <Loader2 className="h-6 w-6 animate-spin text-[#FFCC00] absolute -bottom-1 -right-1" />
            </div>
            <p className="font-display text-xl font-black tracking-tight" data-testid="point-directo-wait-msg">
              {status === "sending" ? "Enviando monto a la terminal física…"
                : (msg || (orderStatus === "at_terminal" ? "La terminal recibió el cobro. Procese la tarjeta." : "Orden creada. Esperando que la terminal la descargue."))}
            </p>
            {orderId && <p className="text-xs text-white/40 font-mono" data-testid="point-directo-order-id">Orden: {orderId}</p>}
            <div className="font-display text-4xl font-black text-[#FFCC00]">${Number(monto || 0).toFixed(2)}</div>
            {status === "waiting" && (
              <div className="flex gap-2 w-full">
                <button onClick={consultarManual} data-testid="point-directo-consultar-btn"
                  className="btn-hard flex-1 h-11 bg-white/5 text-white border border-white/20 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Consultar estado</button>
                <button onClick={cancelarPendiente} data-testid="point-directo-cancelar-btn"
                  className="btn-hard flex-1 h-11 bg-white/5 text-[#F87171] border border-[#F87171]/40 font-bold uppercase tracking-wider text-xs">Cancelar cobro</button>
              </div>
            )}
          </div>
        )}

        {status === "timeout" && (
          <div className="py-6 flex flex-col items-center text-center gap-4" data-testid="point-directo-timeout">
            <Clock className="h-14 w-14 text-[#FFCC00]" strokeWidth={1.5} />
            <p className="font-display text-lg font-black tracking-tight text-[#FFCC00]">Terminal no ha recibido la orden</p>
            <p className="text-sm text-white/60">La orden fue creada, pero la terminal aún no la ha recibido. Revise la conexión y presione Actualizar.</p>
            {orderId && <p className="text-xs text-white/40 font-mono">Orden: {orderId}</p>}
            <div className="flex gap-2 w-full">
              <button onClick={consultarManual} data-testid="point-directo-actualizar-btn"
                className="btn-hard flex-1 h-12 bg-[#009EE3] text-white border border-[#009EE3] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-1"><RefreshCw className="h-4 w-4" /> Actualizar</button>
              <button onClick={cancelarPendiente} data-testid="point-directo-cancelar-timeout-btn"
                className="btn-hard flex-1 h-12 bg-white/5 text-[#F87171] border border-[#F87171]/40 font-bold uppercase tracking-wider text-sm">Cancelar</button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="py-8 flex flex-col items-center text-center gap-4" data-testid="point-directo-error">
            <XCircle className="h-16 w-16 text-[#F87171]" strokeWidth={1.5} />
            <p className="font-display text-xl font-black tracking-tight text-[#F87171]">El cobro no se completó</p>
            <p className="text-sm text-white/60" data-testid="point-directo-error-msg">{msg || "Rechazado o cancelado en la terminal. Puedes reintentar."}</p>
            <div className="flex gap-2 w-full">
              <button onClick={resetState} data-testid="point-directo-retry-btn"
                className="btn-hard flex-1 h-12 bg-[#009EE3] text-white border border-[#009EE3] font-bold uppercase tracking-wider text-sm">Reintentar</button>
              <button onClick={cerrar} data-testid="point-directo-cerrar-btn"
                className="btn-hard flex-1 h-12 bg-white/5 text-white border border-white/20 font-bold uppercase tracking-wider text-sm">Cerrar</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
